import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Group,
  TextInput,
  Checkbox,
  Button,
  Table,
  Pagination,
  ActionIcon,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { ReactComponent as XIcon } from "@/assets/icons/x.svg";
import { ReactComponent as BackIcon } from "@/assets/icons/back.svg";
import { ReactComponent as NextIcon } from "@/assets/icons/next.svg";
import { ReactComponent as DeleteIcon } from "@/assets/icons/delete.svg";
import { ReactComponent as SearchIcon } from "@/assets/icons/search.svg";
import AreYouSureModal from "@/components/Shared/AreYouSureModal/AreYouSureModal";
import UserMobileCard from "@/components/Admin/Users/UserMobileCard";
import UserTableRow from "@/components/Admin/Users/UserTableRow";
import { findActiveSubscription } from "@/helpers/issuesAndSubscriptions";
import {
  successNotification,
  errorNotification,
} from "@/helpers/notifications";
import { chunk } from "@/helpers/common";
import { UserApi } from "@/api/user.api";
import clsx from "clsx";
import { UserWithSubscriptions } from "@/api/types";
import "./Users.css";

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [userFilter, setUserFilter] = useState<string[]>(["all"]);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [users, setUsers] = useState<UserWithSubscriptions[]>([]);
  const [page, setPage] = useState(1);
  const [opened, { open, close }] = useDisclosure(false);

  const getAllUsers = async () => {
    const response = await UserApi.getAllUsers();
    if (response?.payload) setUsers(response.payload);
  };

  useEffect(() => {
    getAllUsers();
  }, []);

  const filteredUsers = users
    .filter((user) => {
      const name = `${user.firstName} ${user.lastName}`;
      const isIncluded = name
        .toLowerCase()
        .includes(searchValue.toLowerCase().trim());
      if (selectedFilter === "all") return isIncluded;
      else if (selectedFilter === "active")
        return (
          user && !!findActiveSubscription(user.userSubscriptions) && isIncluded
        );
      else if (selectedFilter === "unactive")
        return (
          user && !findActiveSubscription(user.userSubscriptions) && isIncluded
        );
      else return;
    })
    .sort((a, b) => b.id - a.id);

  const totalPages = Math.ceil(filteredUsers.length / 10);
  const usersByPage = chunk(filteredUsers, 10);

  const setFilter = (newValue: string[]) => {
    const length = newValue.length;
    setUserFilter(length > 0 ? [newValue[length - 1]] : []);
    setSelectedFilter(length > 0 ? newValue[length - 1] : "");
  };

  const handleSelectAll = () => {
    if (selectedRows.length === filteredUsers.length) {
      setSelectedRows([]);
    } else setSelectedRows(filteredUsers.map((user) => user.id));
  };

  const deleteUsers = async () => {
    close();
    try {
      const res = await UserApi.deleteMultipleUsers(selectedRows);
      if (res?.isSuccessful) {
        successNotification({
          message: res.message,
        });
        getAllUsers();
      } else {
        errorNotification({
          message:
            res.message ?? "Došlo je do pogreške prilikom brisanja korisnika.",
        });
      }
    } catch (error) {
      errorNotification({
        message: "Došlo je do pogreške prilikom brisanja korisnika.",
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <div className="px-[5%]">
      <Group className="py-[1.5rem]" justify="space-between">
        <div className="text-[#0b1a29] text-2xl font-bold">Korisnici</div>
        <TextInput
          value={searchValue}
          placeholder="Pretraži korisnike"
          leftSection={<SearchIcon />}
          className="search-input"
          rightSection={
            searchValue && (
              <ActionIcon
                variant="subtle"
                aria-label="clear"
                size="sm"
                radius="xl"
                onClick={() => setSearchValue("")}
              >
                <XIcon width={10} />
              </ActionIcon>
            )
          }
          onChange={(event) => {
            setSearchValue(event.currentTarget.value);
          }}
        />
      </Group>
      <div className="flex flex-col gap-6 pb-[1.5rem]">
        <Group justify="space-between">
          <Checkbox.Group
            value={userFilter}
            onChange={setFilter}
            labelProps={{
              style: userFilter.includes(selectedFilter)
                ? { color: "#004a99" }
                : { color: "#ffffff" },
            }}
          >
            <div className="filters-container">
              <Checkbox
                value="all"
                label="Svi korisnici"
                color="#004a99"
                className={clsx(
                  "filter filter-one",
                  selectedFilter === "all"
                    ? "text-[#004a99] font-bold"
                    : "text-[#000000]"
                )}
                classNames={{
                  input: "!cursor-pointer",
                  label: "!cursor-pointer",
                }}
              />
              <Checkbox
                value="active"
                label="S aktivnom pretplatom"
                color="#004a99"
                className={clsx(
                  "filter filter-two",
                  selectedFilter === "active"
                    ? "text-[#004a99] font-bold"
                    : "text-[#000000]"
                )}
                classNames={{
                  input: "!cursor-pointer",
                  label: "!cursor-pointer",
                }}
              />
              <Checkbox
                value="unactive"
                label="Bez pretplate"
                color="#004a99"
                className={clsx(
                  "filter filter-three",
                  selectedFilter === "unactive"
                    ? "text-[#004a99] font-bold"
                    : "text-[#000000]"
                )}
                classNames={{
                  input: "!cursor-pointer",
                  label: "!cursor-pointer",
                }}
              />
            </div>
          </Checkbox.Group>
          {!!selectedRows.length && (
            <Button
              variant="transparent"
              color="#f40c0c"
              leftSection={<DeleteIcon />}
              onClick={() => open()}
            >
              Obriši
            </Button>
          )}
        </Group>
        <div className="table-view border border-[#eaeaea] rounded-lg overflow-hidden">
          <Table>
            <Table.Thead className="bg-[#f9f9f9]">
              <Table.Tr>
                <Table.Th>
                  <Checkbox
                    aria-label="Select all"
                    color="#004a99"
                    classNames={{
                      input: "!cursor-pointer",
                    }}
                    checked={!!selectedRows.length}
                    indeterminate={
                      !!selectedRows.length &&
                      selectedRows.length < filteredUsers.length
                    }
                    onChange={handleSelectAll}
                  />
                </Table.Th>
                <Table.Th className="!p-4">Ime i prezime</Table.Th>
                <Table.Th>E-mail</Table.Th>
                <Table.Th className="!text-center">Godišnja pretplata</Table.Th>
                <Table.Th>Datum aktivacije</Table.Th>
                <Table.Th>Datum isteka</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {usersByPage[page - 1]?.length > 0 ? (
                usersByPage[page - 1]?.map((user) => (
                  <UserTableRow
                    key={user.id}
                    user={user}
                    selectedRows={selectedRows}
                    onSelectRow={(id) => {
                      setSelectedRows((prev) =>
                        prev.includes(id)
                          ? prev.filter((rowId) => rowId !== id)
                          : [...prev, id]
                      );
                    }}
                    onClickRow={(id) => navigate(`/admin/users/${id}`)}
                  />
                ))
              ) : (
                <Table.Tr>
                  <Table.Td
                    colSpan={6}
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    Nema podataka...
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </div>
        <div className="card-view">
          {usersByPage[page - 1]?.length > 0 ? (
            usersByPage[page - 1]?.map((user) => (
              <UserMobileCard
                key={user.id}
                user={user}
                onClick={(id) => navigate(`/admin/users/${id}`)}
              />
            ))
          ) : (
            <div>Nema podataka...</div>
          )}
        </div>
        <Group justify="center">
          <Pagination
            total={totalPages}
            color="#004A99"
            value={page}
            onChange={(event) => {
              setPage(event);
              scrollToTop();
            }}
            nextIcon={NextIcon}
            previousIcon={BackIcon}
          />
        </Group>
      </div>
      <AreYouSureModal
        opened={opened}
        message="Jeste li sigurni da želite obrisati odabrane račune? Korisnici ih
        više neće moći koristiti!"
        onClose={close}
        onAction={deleteUsers}
      />
    </div>
  );
};

export default Users;
